/**
 * HESAP PANELİ — parola değiştirme ve GM unvanı.
 *
 * NEDEN VAR: Parolayı değiştirmenin başka yolu yoktu (veritabanına elle
 * müdahale). Kullanıcı parolasını bize ya da birine söylemek zorunda kalmasın.
 *
 * KURALLAR
 *   1. ESKİ PAROLA ZORUNLU. Sunucu doğrular; burada yalnızca istek atılır.
 *   2. YENİ PAROLA İKİ KEZ YAZILIR. Yazım hatası en sık neden; kullanıcı
 *      "şifrem yanlış" sanıp saatler harcamasın.
 *   3. ALANLAR DEĞİŞTİRİLİNCE TEMİZLENİR. Eski parola ekranda kalırsa
 *      omuz sörülerek görülebilir.
 *   4. GÖRÜNEN AD (GM unvanı) parola doğrulaması istemez: hesabı ele
 *      geçirmeye yetmez ve ek engel kullanıcıyı zorlar.
 */

import type { AuthState, Repository } from '@tttrpg/db/client'
import { useState } from 'react'

export interface AccountPanelProps {
  repository: Repository
  auth: AuthState
  /** Kaydedildiğinde çağrılır — üst çubuktaki isim güncellensin. */
  onChanged?: () => void
}

export function AccountPanel({ repository, auth, onChanged }: AccountPanelProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [displayName, setDisplayName] = useState(auth.displayName ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)

    // Yazım hatası sunucuya gitmeden yakalanır.
    if (newPassword !== repeatPassword) {
      setError('Yeni parolalar aynı değil.')
      return
    }
    if (newPassword.length < 8) {
      setError('Parola en az 8 karakter olmalı.')
      return
    }

    setBusy(true)
    const result = await repository.changePassword(currentPassword, newPassword)
    setBusy(false)

    if (!result.ok) {
      setError(result.error)
      return
    }
    setDone('Parola değiştirildi.')
    // Alanları temizle: eski parola ekranda kalmasın.
    setCurrentPassword('')
    setNewPassword('')
    setRepeatPassword('')
  }

  async function submitName(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    const trimmed = displayName.trim()
    if (trimmed.length === 0) {
      setError('Görünen ad boş olamaz.')
      return
    }

    setBusy(true)
    const result = await repository.setDisplayName(trimmed)
    setBusy(false)

    if (!result.ok) {
      setError(result.error)
      return
    }
    setDone('Görünen ad değiştirildi.')
    onChanged?.()
  }

  return (
    <div className="space-y-8">
      {/* --- GÖRÜNEN AD --- */}
      <section>
        <h3 className="mb-1 font-display text-sm tracking-[0.2em] text-gold uppercase">
          Görünen ad
        </h3>
        <p className="mb-3 text-xs text-ink-faint">
          Masada ve atış günlüğünde bu ad görünür. GM isen unvanını yaz — örneğin “Boşluğun
          Hükümdarı”.
        </p>
        <form onSubmit={submitName} className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={60}
            placeholder="Görünen ad"
            aria-label="Görünen ad"
            className="min-w-48 flex-1 rounded border border-line-soft bg-surface-2 px-2 py-1 text-sm text-ink"
          />
          <button
            type="submit"
            disabled={busy}
            className="cursor-pointer rounded border border-gold-dim px-3 py-1 text-xs text-gold transition-colors hover:bg-gold/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            kaydet
          </button>
        </form>
      </section>

      {/* --- PAROLA --- */}
      <section className="border-t border-line-soft pt-6">
        <h3 className="mb-1 font-display text-sm tracking-[0.2em] text-gold uppercase">Parola</h3>
        <p className="mb-3 text-xs text-ink-faint">
          Parolanı kimseyle paylaşmana gerek yok. Kendin değiştir.
        </p>
        <form onSubmit={submitPassword} className="max-w-sm space-y-2">
          <label className="block text-xs text-ink-faint">
            Mevcut parola
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full rounded border border-line-soft bg-surface-2 px-2 py-1 text-sm text-ink"
            />
          </label>
          <label className="block text-xs text-ink-faint">
            Yeni parola (en az 8 karakter)
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded border border-line-soft bg-surface-2 px-2 py-1 text-sm text-ink"
            />
          </label>
          <label className="block text-xs text-ink-faint">
            Yeni parola (tekrar)
            <input
              type="password"
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded border border-line-soft bg-surface-2 px-2 py-1 text-sm text-ink"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="cursor-pointer rounded border border-gold-dim px-3 py-1 text-xs text-gold transition-colors hover:bg-gold/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'kaydediliyor…' : 'parolayı değiştir'}
          </button>
        </form>
      </section>

      {error && (
        <p
          role="alert"
          className="rounded border border-blood/40 bg-blood/10 px-2 py-1 text-xs text-blood-bright"
        >
          {error}
        </p>
      )}
      {done && (
        <p
          role="status"
          className="rounded border border-vital/40 bg-vital/10 px-2 py-1 text-xs text-vital"
        >
          {done}
        </p>
      )}

      <p className="text-[0.65rem] text-ink-faint">
        Oturum: {auth.email} · rol: {auth.role === 'owner' ? 'sahip' : 'oyuncu'}
      </p>
    </div>
  )
}
